import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getDocumentTypes } from "../../api/apiService";
import { SelectField, InputField, Button } from "../../components";
import { useValidationForm } from "../hooks/useValidationForm";
import { useServiceData } from "../../context/ServiceContext";
import { RECAPTCHA_SITE_KEY } from "../../constants/config";
import { SkeletonField } from "../../components/SkeletonLoader/SkeletonField";
import ReCAPTCHA from "react-google-recaptcha";
import styles from "../MainForm/Form.module.css";
import "../../styles/animations.css";

interface ValidationFormProps {
  wasValidated: boolean;
  onValidate: (values: any) => void;
  initialReCaptcha: string | null;
  onRecaptchaChange: (token: string | null) => void;
}

export const ValidationForm: React.FC<ValidationFormProps> = ({
  wasValidated,
  onValidate,
  initialReCaptcha,
  onRecaptchaChange,
}) => {
  const { t } = useTranslation();
  const { setDocumentTypes } = useServiceData();

  const { data: documentTypes, isLoading } = useQuery({
    queryKey: ["document-types"],
    queryFn: getDocumentTypes,
  });

  React.useEffect(() => {
    if (documentTypes) setDocumentTypes(documentTypes);
  }, [documentTypes, setDocumentTypes]);

  const { formik } = useValidationForm(onValidate);

  return (
    <form onSubmit={formik.handleSubmit} className="fadeIn">
      <div className={styles.formRow}>
        <div className={styles.formColumn}>
          {documentTypes && (
            <SelectField
              id="documentType"
              label={t("field.documentType")}
              name="documentType"
              options={documentTypes}
              disabled={wasValidated}
              touched={formik.touched}
              errors={formik.errors}
              getFieldProps={formik.getFieldProps}
            />
          )}

          {isLoading && <SkeletonField />}

          <InputField
            id="documentNumber"
            type="text"
            label={t("field.documentNumber")}
            name="documentNumber"
            placeholder={t("field.phone.placeholder")}
            disabled={wasValidated}
            getFieldProps={formik.getFieldProps}
            touched={formik.touched}
            errors={formik.errors}
          />

          {!wasValidated && (
            <>
              <ReCAPTCHA
                className={styles.recaptchaContainer}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={onRecaptchaChange}
              />
              <div className={styles.justifyContentCenter}>
                <Button
                  type="submit"
                  disabled={
                    !formik.isValid || !formik.dirty || !initialReCaptcha
                  }
                  isLoading={formik.isSubmitting}
                >
                  {t("form.button")}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </form>
  );
};
